import type { LabConfig, TestResult } from '../types';

const GROUP = 'A_environment';

function getWebGLContext(): { gl: WebGLRenderingContext | WebGL2RenderingContext; version: number } | null {
  const canvas = document.createElement('canvas');
  const gl2 = canvas.getContext('webgl2') as WebGL2RenderingContext | null;
  if (gl2) return { gl: gl2, version: 2 };
  const gl1 = canvas.getContext('webgl') as WebGLRenderingContext | null;
  if (gl1) return { gl: gl1, version: 1 };
  return null;
}

function getWebGLInfo(gl: WebGLRenderingContext | WebGL2RenderingContext) {
  const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
  return {
    version: gl.getParameter(gl.VERSION),
    vendor: gl.getParameter(gl.VENDOR),
    renderer: gl.getParameter(gl.RENDERER),
    shadingLanguageVersion: gl.getParameter(gl.SHADING_LANGUAGE_VERSION),
    unmaskedVendor: debugInfo ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) : null,
    unmaskedRenderer: debugInfo ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) : null,
    maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE),
    maxViewportDims: gl.getParameter(gl.MAX_VIEWPORT_DIMS),
    maxCubeMapSize: gl.getParameter(gl.MAX_CUBE_MAP_TEXTURE_SIZE),
    maxRenderbufferSize: gl.getParameter(gl.MAX_RENDERBUFFER_SIZE),
    maxVertexAttribs: gl.getParameter(gl.MAX_VERTEX_ATTRIBS),
    maxVertexUniformVectors: gl.getParameter(gl.MAX_VERTEX_UNIFORM_VECTORS),
    maxFragmentUniformVectors: gl.getParameter(gl.MAX_FRAGMENT_UNIFORM_VECTORS),
    maxTextureImageUnits: gl.getParameter(gl.MAX_TEXTURE_IMAGE_UNITS),
    maxVertexTextureImageUnits: gl.getParameter(gl.MAX_VERTEX_TEXTURE_IMAGE_UNITS),
    extensions: gl.getSupportedExtensions(),
  };
}

async function getBatteryInfo() {
  const supported = 'getBattery' in navigator;
  if (!supported) return { supported, available: false, error: 'navigator.getBattery is not available' };
  try {
    const battery = await (navigator as any).getBattery();
    return {
      supported,
      available: true,
      charging: battery.charging,
      level: battery.level,
      chargingTime: Number.isFinite(battery.chargingTime) ? battery.chargingTime : null,
      dischargingTime: Number.isFinite(battery.dischargingTime) ? battery.dischargingTime : null,
    };
  } catch (err) {
    return { supported, available: false, error: err instanceof Error ? err.message : String(err) };
  }
}

function getNetworkInfo() {
  const conn = (navigator as any).connection;
  if (!conn) return null;
  return {
    effectiveType: conn.effectiveType,
    downlink: conn.downlink,
    rtt: conn.rtt,
    saveData: conn.saveData,
    type: conn.type,
  };
}

function checkApi(name: string): boolean {
  switch (name) {
    case 'webdriver': return !!(navigator as any).webdriver;
    case 'webgpu': return 'gpu' in navigator;
    case 'webgl2': return !!document.createElement('canvas').getContext('webgl2');
    case 'wasm': return typeof WebAssembly === 'object' && typeof WebAssembly.instantiate === 'function';
    case 'wasmSimd': {
      try {
        return WebAssembly.validate(new Uint8Array([
          0x00, 0x61, 0x73, 0x6d, 0x01, 0x00, 0x00, 0x00,
          0x01, 0x05, 0x01, 0x60, 0x00, 0x01, 0x7b, 0x03,
          0x02, 0x01, 0x00, 0x07, 0x0b, 0x01, 0x07, 0x61,
          0x63, 0x74, 0x75, 0x61, 0x6c, 0x6c, 0x79, 0x00,
          0x00, 0x0a, 0x0a, 0x01, 0x08, 0x00, 0xfd, 0x0f,
          0xfd, 0x0f, 0xfd, 0x0f, 0x0b,
        ]));
      } catch { return false; }
    }
    case 'sharedArrayBuffer': {
      try { new SharedArrayBuffer(4); return true; } catch { return false; }
    }
    case 'webcodecs': return 'VideoDecoder' in window;
    case 'webrtc': return 'RTCPeerConnection' in window;
    case 'webusb': return 'usb' in navigator;
    case 'webhid': return 'hid' in navigator;
    case 'webserial': return 'serial' in navigator;
    case 'gamepad': return 'getGamepads' in navigator;
    case 'bluetooth': return 'bluetooth' in navigator;
    case 'permissions': return 'permissions' in navigator;
    default: return false;
  }
}

async function getPermissionState(name: string): Promise<string> {
  if (!('permissions' in navigator)) return 'unsupported';
  try {
    return (await navigator.permissions.query({ name: name as PermissionName })).state;
  } catch (err) {
    return `error: ${err instanceof Error ? err.message : String(err)}`;
  }
}

async function probeSensorConstructor(ctorName: string): Promise<Record<string, unknown>> {
  const Ctor = (window as any)[ctorName];
  const info: Record<string, unknown> = {
    supported: !!Ctor,
    constructed: false,
    started: false,
    reading: null,
    error: null,
  };
  if (!Ctor) return info;

  try {
    const sensor = new Ctor({ frequency: 10 });
    info.constructed = true;

    await new Promise<void>((resolve) => {
      const timer = window.setTimeout(() => {
        try { sensor.stop(); } catch { /* noop */ }
        resolve();
      }, 350);

      sensor.onreading = () => {
        info.started = true;
        info.reading = {
          x: Number.isFinite(sensor.x) ? sensor.x : null,
          y: Number.isFinite(sensor.y) ? sensor.y : null,
          z: Number.isFinite(sensor.z) ? sensor.z : null,
          illuminance: Number.isFinite(sensor.illuminance) ? sensor.illuminance : null,
          quaternion: sensor.quaternion ? Array.from(sensor.quaternion) : null,
        };
        window.clearTimeout(timer);
        try { sensor.stop(); } catch { /* noop */ }
        resolve();
      };

      sensor.onerror = (event: any) => {
        info.error = event?.error ? `${event.error.name}: ${event.error.message}` : 'sensor error';
        window.clearTimeout(timer);
        try { sensor.stop(); } catch { /* noop */ }
        resolve();
      };

      try { sensor.start(); }
      catch (err) {
        info.error = err instanceof Error ? err.message : String(err);
        window.clearTimeout(timer);
        resolve();
      }
    });
  } catch (err) {
    info.error = err instanceof Error ? err.message : String(err);
  }

  return info;
}

async function detectSensors(): Promise<Record<string, unknown>> {
  const sensorNames = ['Accelerometer', 'Gyroscope', 'Magnetometer', 'AbsoluteOrientationSensor', 'RelativeOrientationSensor', 'GravitySensor', 'LinearAccelerationSensor', 'AmbientLightSensor'];
  const sensorEntries = await Promise.all(sensorNames.map(async (name) => [name, await probeSensorConstructor(name)] as const));
  return {
    secureContext: window.isSecureContext,
    events: {
      deviceOrientation: 'DeviceOrientationEvent' in window,
      deviceMotion: 'DeviceMotionEvent' in window,
      requestMotionPermission: !!(window as any).DeviceMotionEvent?.requestPermission,
      requestOrientationPermission: !!(window as any).DeviceOrientationEvent?.requestPermission,
    },
    permissions: {
      accelerometer: await getPermissionState('accelerometer'),
      gyroscope: await getPermissionState('gyroscope'),
      magnetometer: await getPermissionState('magnetometer'),
      ambientLightSensor: await getPermissionState('ambient-light-sensor'),
    },
    genericSensors: Object.fromEntries(sensorEntries),
  };
}

function getFontList(): string[] {
  const baseFonts = ['monospace', 'sans-serif', 'serif'];
  const testFonts = [
    'Arial', 'Arial Black', 'Arial Narrow', 'Calibri', 'Cambria', 'Comic Sans MS',
    'Consolas', 'Courier New', 'Georgia', 'Helvetica', 'Impact', 'Lucida Console',
    'Lucida Sans Unicode', 'Microsoft Sans Serif', 'Palatino Linotype', 'Segoe UI',
    'Tahoma', 'Times New Roman', 'Trebuchet MS', 'Verdana', 'Geneva', 'Charcoal',
    'Menlo', 'Monaco', 'SF Pro Display', 'SF Mono', 'Helvetica Neue', 'Apple SD Gothic Neo',
    'Linux Libertine', 'Liberation Sans', 'DejaVu Sans', 'Ubuntu', 'Noto Sans',
    'Roboto', 'Droid Sans', 'Open Sans', 'Source Sans Pro',
  ];

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;
  const testString = 'mmmmmmmmmmlli';

  const baseMetrics: Record<string, { width: number; height: number }> = {};
  for (const base of baseFonts) {
    ctx.font = `72px ${base}`;
    const m = ctx.measureText(testString);
    baseMetrics[base] = { width: m.width, height: m.actualBoundingBoxAscent + m.actualBoundingBoxDescent };
  }

  const detected: string[] = [];
  for (const font of testFonts) {
    for (const base of baseFonts) {
      ctx.font = `72px "${font}", ${base}`;
      const m = ctx.measureText(testString);
      if (m.width !== baseMetrics[base].width || m.actualBoundingBoxAscent + m.actualBoundingBoxDescent !== baseMetrics[base].height) {
        detected.push(font);
        break;
      }
    }
  }
  return [...new Set(detected)];
}

export async function runEnvironmentTests(config: LabConfig): Promise<TestResult[]> {
  const t0 = performance.now();
  const tests: TestResult[] = [];

  if (config.enableWebGL) {
    try {
      const ctx = getWebGLContext();
      tests.push({
        name: 'webgl_info',
        group: GROUP,
        success: ctx !== null,
        durationMs: performance.now() - t0,
        metadata: ctx ? getWebGLInfo(ctx.gl) : undefined,
        error: ctx ? undefined : 'WebGL not supported',
      });
    } catch (err) {
      tests.push({
        name: 'webgl_info',
        group: GROUP,
        success: false,
        durationMs: performance.now() - t0,
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }

  tests.push({
    name: 'navigator_screen',
    group: GROUP,
    success: true,
    durationMs: performance.now() - t0,
    metadata: {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      hardwareConcurrency: navigator.hardwareConcurrency,
      deviceMemory: (navigator as any).deviceMemory ?? null,
      language: navigator.language,
      languages: navigator.languages,
      cookieEnabled: navigator.cookieEnabled,
      pdfViewerEnabled: (navigator as any).pdfViewerEnabled ?? null,
      screen: {
        width: screen.width,
        height: screen.height,
        availWidth: screen.availWidth,
        availHeight: screen.availHeight,
        colorDepth: screen.colorDepth,
        pixelDepth: screen.pixelDepth,
      },
      devicePixelRatio: window.devicePixelRatio,
      maxTouchPoints: navigator.maxTouchPoints,
      pointer: {
        fine: window.matchMedia('(pointer: fine)').matches,
        coarse: window.matchMedia('(pointer: coarse)').matches,
      },
    },
  });

  if (config.mode !== 'prod') {
    tests.push({
      name: 'battery',
      group: GROUP,
      success: true,
      durationMs: performance.now() - t0,
      metadata: (await getBatteryInfo()) ?? undefined,
    });
  }

  if (config.enableNetwork) {
    tests.push({
      name: 'network',
      group: GROUP,
      success: true,
      durationMs: performance.now() - t0,
      metadata: getNetworkInfo() ?? undefined,
    });
  }

  if (config.enableSensors) {
    tests.push({
      name: 'sensors',
      group: GROUP,
      success: true,
      durationMs: performance.now() - t0,
      metadata: await detectSensors(),
    });
  }

  tests.push({
    name: 'api_availability',
    group: GROUP,
    success: true,
    durationMs: performance.now() - t0,
    metadata: {
      webdriver: checkApi('webdriver'),
      webgpu: checkApi('webgpu'),
      webgl2: checkApi('webgl2'),
      wasm: checkApi('wasm'),
      wasmSimd: checkApi('wasmSimd'),
      sharedArrayBuffer: checkApi('sharedArrayBuffer'),
      webcodecs: checkApi('webcodecs'),
      webrtc: checkApi('webrtc'),
      webusb: checkApi('webusb'),
      webhid: checkApi('webhid'),
      webserial: checkApi('webserial'),
      gamepad: checkApi('gamepad'),
      bluetooth: checkApi('bluetooth'),
      permissions: checkApi('permissions'),
    },
  });

  if (config.mode !== 'prod') {
    try {
      const detectedFonts = getFontList();
      tests.push({
        name: 'font_fingerprint',
        group: GROUP,
        success: true,
        durationMs: performance.now() - t0,
        metadata: { detectedFonts, count: detectedFonts.length },
      });
    } catch (err) {
      tests.push({
        name: 'font_fingerprint',
        group: GROUP,
        success: false,
        durationMs: performance.now() - t0,
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }

  return tests;
}
